/**
 * Centralized API service for BrandHives.
 * Handles auth headers, session tokens, and error handling.
 *
 * Usage:
 *   import api from '../services/api';
 *   const data = await api.get('/app/api/user/profile');
 *   await api.post('/app/api/user/settings', { timezone: 'America/Toronto' });
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://go.brandhives.com';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('nectar_st') || null;
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('nectar_st', token);
    } else {
      localStorage.removeItem('nectar_st');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('nectar_st');
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('nectar_st');
  }

  async request(method, path, body = null, options = {}) {
    const headers = { 'Content-Type': 'application/json' };
    const token = this.getToken();
    if (token) {
      headers['X-Session-Token'] = token;
    }

    const config = { method, headers, credentials: 'include', ...options };
    if (body && method !== 'GET') {
      config.body = JSON.stringify(body);
    }

    const response = await fetch(`${API_URL}${path}`, config);

    if (response.status === 401 && !path.includes('/auth/')) {
      // Session expired — existing fetch interceptor handles busy-bee
      return null;
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = new Error(data.error || `HTTP ${response.status}`);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  get(path, options) { return this.request('GET', path, null, options); }
  post(path, body, options) { return this.request('POST', path, body, options); }
  patch(path, body, options) { return this.request('PATCH', path, body, options); }
  put(path, body, options) { return this.request('PUT', path, body, options); }
  delete(path, options) { return this.request('DELETE', path, null, options); }
}

export const api = new ApiService();
export default api;
